import React, { useEffect, useState } from "react";
import { getSf009AddedByDisplay } from "./sf009AddedByDisplay";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getRawSf009FormData } from "./sf009FormData";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./horizonTable.css";
import "../forms/formTitle.css";
import { applyFooterAllPages } from "../../utils/formPdfHelpers.js";
import onsiteproLogo from "../../assets/logo2 2.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { parseSiteRegisterDate } from "./adminEditModalTheme";
import { ensureFormUuid } from "../../utils/formUuid";
import {
  buildSf009SharePointFileName,
  createSf009SharePointUploadConfig,
  getSf009SharePointFields,
  postSf009PdfToSharePoint,
} from "./sf009SharePointUpload";
import Sf009TimeSelect from "./Sf009TimeSelect";
import ThemeYnSelect from "../forms/ThemeYnSelect";
import { normalizeSf009Time } from "./sf009FormSignatureUtils";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";
const THEME_RGB = [139, 22, 52];
const THEME_LIGHT = "rgb(239, 226, 231)";
const THEME_LIGHT_RGB = [239, 226, 231];
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";
const PDF_WHITE = [255, 255, 255];
const PDF_PAGE_BOTTOM = 32;
const REGISTER_ROW_COUNT = 12;
function normalizeWorkCompleteYN(val) {
  const s = String(val ?? "").trim();
  if (!s) return "";
  const u = s.toUpperCase();
  if (u === "Y" || u === "YES") return "Y";
  if (u === "N" || u === "NO") return "N";
  return s;
}

const EMPTY_ISSUED = { date: "", time: "" };
const EMPTY_SUSPENDED = { date: "", time: "" };
const EMPTY_CANCELLED = { date: "", time: "", initial: "" };

function emptyRegisterRow() {
  return {
    permitType: "",
    permitNo: "",
    issued: { ...EMPTY_ISSUED },
    issueTo: "",
    issueBy: "",
    workCompleteYN: "",
    workCompleteSuspended: { ...EMPTY_SUSPENDED },
    cancelled: { ...EMPTY_CANCELLED },
  };
}

const DEFAULT_FORM_DATA = {
  contractName: "",
  contractNumber: "",
  permitCoordinator: "",
  forEntity: "TTSL",
  registerEntries: [],
};

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function formatEntryDateDisplay(value) {
  if (value == null || value === "") return "";
  if (value instanceof Date && !isNaN(value.getTime())) {
    const dd = String(value.getDate()).padStart(2, "0");
    const mm = String(value.getMonth() + 1).padStart(2, "0");
    const yyyy = value.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }
  const s = String(value).trim();
  if (!s) return "";
  const dmy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmy) {
    return `${String(dmy[1]).padStart(2, "0")}/${String(dmy[2]).padStart(2, "0")}/${dmy[3]}`;
  }
  const parsed = parseDateForPicker(s);
  if (parsed) return formatEntryDateDisplay(parsed);
  return s;
}

function normalizeRegisterRow(src) {
  const row = src && typeof src === "object" ? src : {};
  const issued = row.issued && typeof row.issued === "object" ? row.issued : {};
  const suspended = row.workCompleteSuspended && typeof row.workCompleteSuspended === "object" ? row.workCompleteSuspended : {};
  const cancelled = row.cancelled && typeof row.cancelled === "object" ? row.cancelled : {};
  return {
    permitType: row.permitType ?? row.permit_type ?? "",
    permitNo: row.permitNo ?? row.permit_no ?? "",
    issued: {
      date: formatEntryDateDisplay(issued.date ?? row.issuedDate ?? ""),
      time: normalizeSf009Time(issued.time ?? row.issuedTime ?? ""),
    },
    issueTo: row.issueTo ?? row.issue_to ?? "",
    issueBy: row.issueBy ?? row.issue_by ?? "",
    workCompleteYN: normalizeWorkCompleteYN(
      row.workCompleteYN ?? row.workCompleteYn ?? row.work_complete_yn ?? ""
    ),
    workCompleteSuspended: {
      date: formatEntryDateDisplay(suspended.date ?? row.suspendedDate ?? ""),
      time: normalizeSf009Time(suspended.time ?? row.suspendedTime ?? ""),
    },
    cancelled: {
      date: formatEntryDateDisplay(cancelled.date ?? row.cancelledDate ?? ""),
      time: normalizeSf009Time(cancelled.time ?? row.cancelledTime ?? ""),
      initial: cancelled.initial ?? cancelled.initl ?? row.cancelledInitial ?? "",
    },
  };
}

function hasRegisterRowData(row) {
  if (!row || typeof row !== "object") return false;
  const issued = row.issued && typeof row.issued === "object" ? row.issued : {};
  const suspended = row.workCompleteSuspended && typeof row.workCompleteSuspended === "object" ? row.workCompleteSuspended : {};
  const cancelled = row.cancelled && typeof row.cancelled === "object" ? row.cancelled : {};
  return [
    row.permitType,
    row.permitNo,
    issued.date,
    issued.time,
    row.issueTo,
    row.issueBy,
    row.workCompleteYN,
    suspended.date,
    suspended.time,
    cancelled.date,
    cancelled.time,
    cancelled.initial,
  ].some((v) => v != null && String(v).trim() !== "");
}

function registerEntriesFromRaw(entries) {
  const list = Array.isArray(entries) ? entries.map(normalizeRegisterRow) : [];
  return list.filter(hasRegisterRowData);
}

function cloneRegisterEntries(entries) {
  return Array.isArray(entries) ? entries.map(normalizeRegisterRow) : [];
}

function entriesForPdf(entries) {
  const filled = registerEntriesFromRaw(entries);
  const rows = [...filled];
  while (rows.length < REGISTER_ROW_COUNT) rows.push(emptyRegisterRow());
  return rows;
}

function mergeRecordIntoDefaults(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const hd = src.headerDetails || {};
  return {
    contractName: src.contractName ?? hd.contractName ?? src.contract?.name ?? "",
    contractNumber: src.contractNumber ?? hd.contractNumber ?? src.contract?.number ?? "",
    permitCoordinator: src.permitCoordinator ?? hd.permitCoordinator ?? "",
    forEntity: src.forEntity ?? hd.forEntity ?? src.for ?? "TTSL",
    registerEntries: registerEntriesFromRaw(src.registerEntries ?? src.entries),
  };
}

function dateToLocalYMD(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function dateToDisplayDMY(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function parseDateForPicker(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const slashOrDash = parseSiteRegisterDate(value);
  if (slashOrDash) return slashOrDash;
  if (typeof value === "string") {
    const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return isNaN(d.getTime()) ? null : d;
    }
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

const sf009TblBorder = `1px solid ${THEME_BORDER}`;
const sf009LblCell = {
  border: sf009TblBorder,
  padding: "8px 10px",
  fontSize: 12,
  fontWeight: 600,
  background: THEME,
  color: "#fff",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};
const sf009ValCell = {
  border: sf009TblBorder,
  padding: "6px 8px",
  fontSize: 12,
  background: "#fff",
  verticalAlign: "middle",
};
const sf009RegHeadCell = {
  border: sf009TblBorder,
  padding: "6px 8px",
  fontSize: 10,
  fontWeight: 600,
  background: THEME_LIGHT,
  color: THEME,
  textAlign: "center",
  verticalAlign: "middle",
};
const sf009RegCell = {
  border: sf009TblBorder,
  padding: "4px 6px",
  fontSize: 11,
  background: "#fff",
  verticalAlign: "middle",
};
const sf009RegTimeCell = {
  ...sf009RegCell,
  minWidth: 96,
  width: 96,
  maxWidth: 110,
  overflow: "visible",
  whiteSpace: "nowrap",
};
const sf009CellInput = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: 11,
  padding: "4px 2px",
  background: "transparent",
  boxSizing: "border-box",
};
function Sf009CSitePermitRegisterPage() {
  const canEditDelete = canEditDeleteRecords();
  const [forms, setForms] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  useEffect(() => {
    fetchForms();
    fetchAppUsers();
  }, []);

  const getRawFormData = getRawSf009FormData;

  async function fetchForms() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-c-site-permit-register", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setForms(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setForms([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = forms.filter((f) => {
    const fd = mergeRecordIntoDefaults(getRawFormData(f));
    const fields = [fd.contractName, fd.contractNumber, fd.permitCoordinator, fd.forEntity, f.name].map((v) =>
      (v || "").toLowerCase()
    );
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const updateFormData = (section, key, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      if (section === "_flat") {
        next.formData[key] = value;
      } else if (section && key) {
        next.formData[section] = { ...(next.formData[section] || {}), [key]: value };
      }
      const fd = next.formData;
      next.name = fd.permitCoordinator || "";
      const firstDate = fd.registerEntries?.[0]?.issued?.date;
      next.date = firstDate || "";
      return next;
    });
  };

  const updateRegisterRow = (rowIndex, field, value, nestedKey) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      const entries = cloneRegisterEntries(next.formData.registerEntries);
      const row = { ...entries[rowIndex] };
      if (nestedKey) {
        row[field] = { ...(row[field] || {}), [nestedKey]: value };
      } else {
        row[field] = value;
      }
      entries[rowIndex] = row;
      next.formData.registerEntries = entries;
      next.name = next.formData.permitCoordinator || "";
      const firstWithDate = entries.find((e) => e?.issued?.date);
      next.date = firstWithDate?.issued?.date || "";
      return next;
    });
  };

  const addRegisterRow = () => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      next.formData.registerEntries = [...cloneRegisterEntries(next.formData.registerEntries), emptyRegisterRow()];
      return next;
    });
  };

  const removeRegisterRow = (rowIndex) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      const entries = cloneRegisterEntries(next.formData.registerEntries);
      entries.splice(rowIndex, 1);
      next.formData.registerEntries = entries;
      const firstWithDate = entries.find((e) => e?.issued?.date);
      next.date = firstWithDate?.issued?.date || "";
      return next;
    });
  };

  const handleEdit = (f) => {
    const raw = getRawFormData(f);
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      formData: mergeRecordIntoDefaults(raw),
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const raw = editForm.formData || {};
      const entriesToSave = registerEntriesFromRaw(raw.registerEntries);
      const res = await fetch(getBaseUrl() + "/api/sf-009-c-site-permit-register/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...raw,
          registerEntries: entriesToSave,
          name: editForm.name || raw.permitCoordinator,
          date: editForm.date || entriesToSave[0]?.issued?.date,
          formUuid: ensureFormUuid(editForm.formUuid),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Form updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-c-site-permit-register/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        toast.success("Form deleted");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const parsed = parseDateForPicker(d);
    if (parsed) return parsed.toLocaleDateString("en-GB");
    try {
      return new Date(d).toLocaleDateString("en-GB");
    } catch {
      return String(d);
    }
  };

  const getUserName = (record) => getSf009AddedByDisplay(record, appUsers);

  const safeDate = (v) => parseDateForPicker(v);

  const fd = editForm?.formData || {};

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? createSf009SharePointUploadConfig(options) : null;

    const recordRaw = getRawFormData(record);
    const raw = mergeRecordIntoDefaults(recordRaw);
    const entries = registerEntriesFromRaw(recordRaw.registerEntries);

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentX = 10;
    const contentW = pageWidth - 20;
    const pdfMargin = { left: contentX, right: contentX, bottom: PDF_PAGE_BOTTOM };
    const headerHeight = 30;

    const safePdfDate = (v) => formatEntryDateDisplay(v);

    doc.setFillColor(...THEME_RGB);
    doc.rect(0, 0, pageWidth, headerHeight, "F");
    try {
      const logoImg = new Image();
      await new Promise((resolve) => {
        logoImg.onload = resolve;
        logoImg.onerror = resolve;
        logoImg.src = onsiteproLogo;
      });
      doc.addImage(logoImg, "PNG", 10, 5, 65, 20);
    } catch {}

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Site Permit Register", 90, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", 90, 19);

    let yPosition = headerHeight + 8;

    const pdfGrid = {
      font: "helvetica",
      fontSize: 7.5,
      cellPadding: 2.5,
      lineWidth: 0.2,
      textColor: [0, 0, 0],
      valign: "middle",
      overflow: "linebreak",
    };
    const pdfLbl = { fillColor: THEME_RGB, fontStyle: "bold", textColor: [255, 255, 255], fontSize: 7.5 };
    const pdfVal = { fillColor: PDF_WHITE, fontSize: 7.5 };
    const pdfRegHead = {
      fillColor: THEME_LIGHT_RGB,
      fontStyle: "bold",
      textColor: THEME_RGB,
      fontSize: 7,
      halign: "center",
      valign: "middle",
    };
    const pdfRegBody = { fillColor: PDF_WHITE, fontSize: 7, valign: "middle", minCellHeight: 10 };

    const regColW = {
      0: contentW * 0.1,
      1: contentW * 0.07,
      2: contentW * 0.08,
      3: contentW * 0.07,
      4: contentW * 0.1,
      5: contentW * 0.1,
      6: contentW * 0.08,
      7: contentW * 0.08,
      8: contentW * 0.07,
      9: contentW * 0.08,
      10: contentW * 0.07,
      11: contentW * 0.1,
    };

    const ensureSpace = (needed = 20) => {
      if (yPosition + needed > pageHeight - PDF_PAGE_BOTTOM) {
        doc.addPage();
        yPosition = headerHeight + 8;
      }
    };

    const addPdfTable = (tableConfig, spaceNeeded = 20) => {
      ensureSpace(spaceNeeded);
      autoTable(doc, {
        theme: "grid",
        tableWidth: contentW,
        margin: pdfMargin,
        rowPageBreak: "auto",
        ...tableConfig,
        startY: yPosition,
      });
      yPosition = doc.lastAutoTable.finalY;
    };

    addPdfTable(
      {
        body: [
          ["Contract: Name:", raw.contractName || "", "Contract Number:", raw.contractNumber || ""],
          ["Name of Permit Coordinator:", raw.permitCoordinator || "", "For:", raw.forEntity || "TTSL"],
        ],
        columnStyles: {
          0: { cellWidth: contentW * 0.22, ...pdfLbl },
          1: { cellWidth: contentW * 0.28, ...pdfVal },
          2: { cellWidth: contentW * 0.22, ...pdfLbl },
          3: { cellWidth: contentW * 0.28, ...pdfVal },
        },
        styles: pdfGrid,
      },
      22
    );

    const registerBody = entries.map((row) => [
      row.permitType || "",
      row.permitNo || "",
      safePdfDate(row.issued?.date),
      row.issued?.time || "",
      row.issueTo || "",
      row.issueBy || "",
      row.workCompleteYN || "",
      safePdfDate(row.workCompleteSuspended?.date),
      row.workCompleteSuspended?.time || "",
      safePdfDate(row.cancelled?.date),
      row.cancelled?.time || "",
      row.cancelled?.initial || "",
    ]);

    if (registerBody.length > 0) {
      ensureSpace(40);
      autoTable(doc, {
        startY: yPosition,
        theme: "grid",
        tableWidth: contentW,
        margin: pdfMargin,
        rowPageBreak: "auto",
        showHead: "firstPage",
        head: [
          [
            { content: "Permit Type:", rowSpan: 2, styles: pdfRegHead },
            { content: "Permit No", rowSpan: 2, styles: pdfRegHead },
            { content: "Issued", colSpan: 2, styles: pdfRegHead },
            { content: "Issue To", rowSpan: 2, styles: pdfRegHead },
            { content: "Issue By", rowSpan: 2, styles: pdfRegHead },
            { content: "Work Complete Y / N", rowSpan: 2, styles: pdfRegHead },
            { content: "Work Complete / suspended", colSpan: 2, styles: pdfRegHead },
            { content: "Cancelled", colSpan: 3, styles: pdfRegHead },
          ],
          [
            { content: "Date", styles: pdfRegHead },
            { content: "Time", styles: pdfRegHead },
            { content: "Date", styles: pdfRegHead },
            { content: "Time", styles: pdfRegHead },
            { content: "Date", styles: pdfRegHead },
            { content: "Time", styles: pdfRegHead },
            { content: "Init'l", styles: pdfRegHead },
          ],
        ],
        body: registerBody,
        styles: pdfGrid,
        bodyStyles: pdfRegBody,
        headStyles: {
          fillColor: THEME_LIGHT_RGB,
          textColor: THEME_RGB,
          fontStyle: "bold",
          fontSize: 7,
          halign: "center",
          valign: "middle",
          lineWidth: 0.2,
        },
        columnStyles: regColW,
      });
      yPosition = doc.lastAutoTable.finalY;
    }

    applyFooterAllPages(doc);

    const displayName = raw.permitCoordinator || record.name || "Register";
        const safeName = String(displayName).replace(/[\\/:"*?<>|]+/g, "-");
    const safeDateStr = String(formatDate(record.date || entries[0]?.issued?.date) || "form").replace(/[\\/:"*?<>|]+/g, "-");
    const fileName =
      shouldUploadToSharePoint && uploadConfig
        ? buildSf009SharePointFileName("C", getSf009SharePointFields("C", raw, record, getUserName))
        : `SF009C_${safeName}_${safeDateStr}.pdf`;

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${raw.contractName || ""}|${record.date || ""}`;
        await postSf009PdfToSharePoint({ doc, fileName, uploadConfig, setUploadingKey, uploadKey: key });
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      }
    } else {
      doc.save(fileName);
      toast.success("SF-009-C Site Permit Register PDF generated successfully!");
    }
  };

  const renderRegisterForm = () => {
    const entries = cloneRegisterEntries(fd.registerEntries);
    return (
      <div style={{ border: sf009TblBorder, background: "#fff" }}>
        <div
          style={{
            textAlign: "center",
            padding: "14px 12px",
            fontWeight: 700,
            fontSize: 15,
            color: "#1a202c",
            borderBottom: sf009TblBorder,
          }}
        >
          Site Permit Register
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <tbody>
            <tr>
              <td style={{ ...sf009LblCell, width: "22%" }}>Contract: Name:</td>
              <td style={{ ...sf009ValCell, width: "28%" }}>
                <input
                  type="text"
                  value={fd.contractName || ""}
                  onChange={(e) => updateFormData("_flat", "contractName", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
              <td style={{ ...sf009LblCell, width: "22%" }}>Contract Number:</td>
              <td style={{ ...sf009ValCell, width: "28%" }}>
                <input
                  type="text"
                  value={fd.contractNumber || ""}
                  onChange={(e) => updateFormData("_flat", "contractNumber", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
            </tr>
            <tr>
              <td style={sf009LblCell}>Name of Permit Coordinator:</td>
              <td style={sf009ValCell}>
                <input
                  type="text"
                  value={fd.permitCoordinator || ""}
                  onChange={(e) => updateFormData("_flat", "permitCoordinator", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
              <td style={sf009LblCell}>For:</td>
              <td style={sf009ValCell}>
                <input
                  type="text"
                  value={fd.forEntity || "TTSL"}
                  onChange={(e) => updateFormData("_flat", "forEntity", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
            </tr>
          </tbody>
        </table>
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 12px", borderTop: sf009TblBorder, background: "#fff" }}>
          <button
            type="button"
            onClick={addRegisterRow}
            style={{
              background: THEME,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            <i className="fa fa-plus" style={{ marginRight: 6 }} />
            Add row
          </button>
        </div>
        <div style={{ overflowX: "auto", borderTop: sf009TblBorder }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
            <thead>
              <tr>
                <th rowSpan={2} style={sf009RegHeadCell}>
                  Permit Type:
                </th>
                <th rowSpan={2} style={sf009RegHeadCell}>
                  Permit No
                </th>
                <th colSpan={2} style={sf009RegHeadCell}>
                  Issued
                </th>
                <th rowSpan={2} style={sf009RegHeadCell}>
                  Issue To
                </th>
                <th rowSpan={2} style={sf009RegHeadCell}>
                  Issue By
                </th>
                <th rowSpan={2} style={sf009RegHeadCell}>
                  Work Complete Y / N
                </th>
                <th colSpan={2} style={sf009RegHeadCell}>
                  Work Complete / suspended
                </th>
                <th colSpan={3} style={sf009RegHeadCell}>
                  Cancelled
                </th>
                <th rowSpan={2} style={{ ...sf009RegHeadCell, width: 48 }}>
                  &nbsp;
                </th>
              </tr>
              <tr>
                <th style={sf009RegHeadCell}>Date</th>
                <th style={{ ...sf009RegHeadCell, minWidth: 96 }}>Time</th>
                <th style={sf009RegHeadCell}>Date</th>
                <th style={{ ...sf009RegHeadCell, minWidth: 96 }}>Time</th>
                <th style={sf009RegHeadCell}>Date</th>
                <th style={{ ...sf009RegHeadCell, minWidth: 96 }}>Time</th>
                <th style={sf009RegHeadCell}>Init&apos;l</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={13} style={{ ...sf009RegCell, textAlign: "center", color: "#718096", padding: 20 }}>
                    No permit rows yet. Click &quot;Add row&quot; to add an entry.
                  </td>
                </tr>
              ) : (
              entries.map((row, idx) => (
                <tr key={idx}>
                  <td style={sf009RegCell}>
                    <input
                      type="text"
                      value={row.permitType || ""}
                      onChange={(e) => updateRegisterRow(idx, "permitType", e.target.value)}
                      style={sf009CellInput}
                    />
                  </td>
                  <td style={sf009RegCell}>
                    <input
                      type="text"
                      value={row.permitNo || ""}
                      onChange={(e) => updateRegisterRow(idx, "permitNo", e.target.value)}
                      style={sf009CellInput}
                    />
                  </td>
                  <td style={sf009RegCell}>
                    <DatePicker
                      selected={safeDate(row.issued?.date)}
                      onChange={(d) => updateRegisterRow(idx, "issued", d ? dateToDisplayDMY(d) : "", "date")}
                      dateFormat="dd-MM-yyyy"
                      className="form-control form-control-sm border-0 p-0"
                      placeholderText="dd-MM-yyyy"
                      wrapperClassName="w-100"
                    />
                  </td>
                  <td style={sf009RegTimeCell}>
                    <Sf009TimeSelect
                      value={row.issued?.time || ""}
                      onChange={(v) => updateRegisterRow(idx, "issued", v, "time")}
                    />
                  </td>
                  <td style={sf009RegCell}>
                    <input
                      type="text"
                      value={row.issueTo || ""}
                      onChange={(e) => updateRegisterRow(idx, "issueTo", e.target.value)}
                      style={sf009CellInput}
                    />
                  </td>
                  <td style={sf009RegCell}>
                    <input
                      type="text"
                      value={row.issueBy || ""}
                      onChange={(e) => updateRegisterRow(idx, "issueBy", e.target.value)}
                      style={sf009CellInput}
                    />
                  </td>
                  <td style={{ ...sf009RegCell, minWidth: 72 }}>
                    <ThemeYnSelect
                      value={normalizeWorkCompleteYN(row.workCompleteYN)}
                      onChange={(v) => updateRegisterRow(idx, "workCompleteYN", normalizeWorkCompleteYN(v))}
                      placeholder="Select"
                      fontSize={11}
                    />
                  </td>
                  <td style={sf009RegCell}>
                    <DatePicker
                      selected={safeDate(row.workCompleteSuspended?.date)}
                      onChange={(d) =>
                        updateRegisterRow(idx, "workCompleteSuspended", d ? dateToDisplayDMY(d) : "", "date")
                      }
                      dateFormat="dd-MM-yyyy"
                      className="form-control form-control-sm border-0 p-0"
                      placeholderText="dd-MM-yyyy"
                      wrapperClassName="w-100"
                    />
                  </td>
                  <td style={sf009RegTimeCell}>
                    <Sf009TimeSelect
                      value={row.workCompleteSuspended?.time || ""}
                      onChange={(v) => updateRegisterRow(idx, "workCompleteSuspended", v, "time")}
                    />
                  </td>
                  <td style={sf009RegCell}>
                    <DatePicker
                      selected={safeDate(row.cancelled?.date)}
                      onChange={(d) => updateRegisterRow(idx, "cancelled", d ? dateToDisplayDMY(d) : "", "date")}
                      dateFormat="dd-MM-yyyy"
                      className="form-control form-control-sm border-0 p-0"
                      placeholderText="dd-MM-yyyy"
                      wrapperClassName="w-100"
                    />
                  </td>
                  <td style={sf009RegTimeCell}>
                    <Sf009TimeSelect
                      value={row.cancelled?.time || ""}
                      onChange={(v) => updateRegisterRow(idx, "cancelled", v, "time")}
                    />
                  </td>
                  <td style={sf009RegCell}>
                    <input
                      type="text"
                      value={row.cancelled?.initial || ""}
                      onChange={(e) => updateRegisterRow(idx, "cancelled", e.target.value, "initial")}
                      style={sf009CellInput}
                    />
                  </td>
                  <td style={{ ...sf009RegCell, textAlign: "center" }}>
                    <button
                      type="button"
                      onClick={() => removeRegisterRow(idx)}
                      title="Remove row"
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#e53e3e",
                        cursor: "pointer",
                        padding: 4,
                      }}
                    >
                      <i className="fa fa-trash" />
                    </button>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-009-C Site Permit Register</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: THEME,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Contract Name</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Contract Number</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Permit Coordinator</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>For</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                    Loading records...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                paged.map((r, idx) => {
                  const raw = mergeRecordIntoDefaults(getRawFormData(r));
                  const updatedDate = r.updatedAt || r.createdAt || r.date;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.contractName || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.contractNumber || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.permitCoordinator || r.name || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.forEntity || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const k = `${r._id || ""}|${raw.contractName || ""}|${r.date || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) {
                                    generatePDF(r, { uploadOnly: true, onSuccess: () => {}, onError: () => {} });
                                  }
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button onClick={() => handleDelete(r._id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button className="horizon-table-btn" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>
            Prev
          </button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>
            {page} / {totalPages}
          </span>
          <button className="horizon-table-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>
            Next
          </button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 980, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit SF-009-C Site Permit Register</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", background: "#f8fafc", padding: 16 }}>
                {renderRegisterForm()}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleEditSave}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-009-C Site Permit Register File"
        importPath="sf-009-c-site-permit-register/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setForms((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-009-C Site Permit Register</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this form?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sf009CSitePermitRegisterPage;
